import { storeS } from '@/store';
import { computed } from 'vue';

/**
 * Composable для глобального управління вікнами тікетів
 * Дозволяє відкривати, закривати, згортати та розгортати вікна тікетів по всьому проєкту
 */
export function useTicketWindows() {
    const ticketWindows = computed(() => storeS.ticketWindows);

    /**
     * Відкрити нове вікно з тікетом
     * @param {Object|Array} tickets - тікет або масив тікетів для відкриття
     * @returns {String} - ID створеного вікна
     */
    const openWindow = (tickets) => {
        const windowId = `window_${storeS.ticketWindows.nextWindowId++}`;

        const ticketsArray = Array.isArray(tickets) ? tickets : [tickets];

        const newWindow = {
            id: windowId,
            tickets: ticketsArray,
            isMinimized: false,
            createdAt: Date.now(),
            activeTabIndex: 0
        };

        storeS.ticketWindows.windows.push(newWindow);
        storeS.ticketWindows.activeWindowId = windowId;

        return windowId;
    };

    /**
     * Закрити вікно
     * @param {String} windowId - ID вікна для закриття
     */
    const closeWindow = (windowId) => {
        const index = storeS.ticketWindows.windows.findIndex(w => w.id === windowId);
        if (index !== -1) {
            storeS.ticketWindows.windows.splice(index, 1);
        }

        // Видалити з мінімізованих, якщо там є
        const minIndex = storeS.ticketWindows.minimizedWindows.findIndex(w => w.id === windowId);
        if (minIndex !== -1) {
            storeS.ticketWindows.minimizedWindows.splice(minIndex, 1);
        }

        // Якщо закрили активне вікно, встановити інше активним
        if (storeS.ticketWindows.activeWindowId === windowId) {
            if (storeS.ticketWindows.windows.length > 0) {
                storeS.ticketWindows.activeWindowId = storeS.ticketWindows.windows[storeS.ticketWindows.windows.length - 1].id;
            } else {
                storeS.ticketWindows.activeWindowId = null;
            }
        }
    };

    /**
     * Згорнути вікно
     * @param {String} windowId - ID вікна для згортання
     */
    const minimizeWindow = (windowId) => {
        const window = storeS.ticketWindows.windows.find(w => w.id === windowId);
        if (window) {
            window.isMinimized = true;

            // Додати до списку мінімізованих
            if (!storeS.ticketWindows.minimizedWindows.find(w => w.id === windowId)) {
                storeS.ticketWindows.minimizedWindows.push({
                    id: windowId,
                    tickets: window.tickets,
                    activeTabIndex: window.activeTabIndex
                });
            }

            // Якщо згорнули активне вікно, вибрати інше
            if (storeS.ticketWindows.activeWindowId === windowId) {
                const nextWindow = storeS.ticketWindows.windows.find(w => !w.isMinimized);
                storeS.ticketWindows.activeWindowId = nextWindow ? nextWindow.id : null;
            }
        }
    };

    /**
     * Розгорнути вікно
     * @param {String} windowId - ID вікна для розгортання
     */
    const restoreWindow = (windowId) => {
        const window = storeS.ticketWindows.windows.find(w => w.id === windowId);
        if (window) {
            window.isMinimized = false;
            storeS.ticketWindows.activeWindowId = windowId;

            // Видалити зі списку мінімізованих
            const minIndex = storeS.ticketWindows.minimizedWindows.findIndex(w => w.id === windowId);
            if (minIndex !== -1) {
                storeS.ticketWindows.minimizedWindows.splice(minIndex, 1);
            }
        }
    };

    /**
     * Встановити активне вікно
     * @param {String} windowId - ID вікна
     */
    const setActiveWindow = (windowId) => {
        const window = storeS.ticketWindows.windows.find(w => w.id === windowId);
        if (window && !window.isMinimized) {
            storeS.ticketWindows.activeWindowId = windowId;
        }
    };

    /**
     * Додати тікет до існуючого вікна
     * @param {String} windowId - ID вікна
     * @param {Object} ticket - Тікет для додавання
     */
    const addTicketToWindow = (windowId, ticket) => {
        const window = storeS.ticketWindows.windows.find(w => w.id === windowId);
        if (window) {
            // Перевірити, чи тікет вже не відкритий
            const ticketExists = window.tickets.find(t => t.ticketId === ticket.ticketId);
            if (!ticketExists) {
                window.tickets.push(ticket);
                window.activeTabIndex = window.tickets.length - 1;
            } else {
                // Якщо тікет вже відкритий, просто активуємо його
                window.activeTabIndex = window.tickets.findIndex(t => t.ticketId === ticket.ticketId);
            }
        }
    };

    /**
     * Закрити таб (тікет) у вікні
     * @param {String} windowId - ID вікна
     * @param {Number} tabIndex - Індекс табу
     */
    const closeTicketTab = (windowId, tabIndex) => {
        const window = storeS.ticketWindows.windows.find(w => w.id === windowId);
        if (window) {
            window.tickets.splice(tabIndex, 1);

            // Якщо не залишилося тікетів, закрити вікно
            if (window.tickets.length === 0) {
                closeWindow(windowId);
            } else {
                // Оновити активний таб
                if (window.activeTabIndex >= window.tickets.length) {
                    window.activeTabIndex = window.tickets.length - 1;
                }
            }
        }
    };

    /**
     * Змінити активний таб у вікні
     * @param {String} windowId - ID вікна
     * @param {Number} tabIndex - Індекс нового активного табу
     */
    const setActiveTab = (windowId, tabIndex) => {
        const window = storeS.ticketWindows.windows.find(w => w.id === windowId);
        if (window && tabIndex >= 0 && tabIndex < window.tickets.length) {
            window.activeTabIndex = tabIndex;
        }
    };

    /**
     * Отримати активне вікно
     * @returns {Object|null} - Активне вікно або null
     */
    const getActiveWindow = () => {
        if (!storeS.ticketWindows.activeWindowId) return null;
        return storeS.ticketWindows.windows.find(w => w.id === storeS.ticketWindows.activeWindowId);
    };

    /**
     * Перевірити, чи тікет вже відкритий
     * @param {Number} ticketId - ID тікету
     * @returns {Object|null} - {windowId, tabIndex} якщо знайдено, інакше null
     */
    const findOpenedTicket = (ticketId) => {
        for (const window of storeS.ticketWindows.windows) {
            const tabIndex = window.tickets.findIndex(t => t.ticketId === ticketId);
            if (tabIndex !== -1) {
                return { windowId: window.id, tabIndex };
            }
        }
        return null;
    };

    /**
     * Відкрити тікет (якщо вже відкритий - активувати, інакше - створити нове вікно)
     * @param {Object} ticket - Тікет для відкриття
     * @returns {String} - ID вікна
     */
    const openTicket = (ticket) => {
        // Перевірити, чи тікет вже відкритий
        const opened = findOpenedTicket(ticket.ticketId);

        if (opened) {
            // Якщо тікет вже відкритий, активувати його
            const window = storeS.ticketWindows.windows.find(w => w.id === opened.windowId);
            if (window) {
                if (window.isMinimized) {
                    restoreWindow(opened.windowId);
                } else {
                    setActiveWindow(opened.windowId);
                }
                setActiveTab(opened.windowId, opened.tabIndex);
                return opened.windowId;
            }
        }

        // Якщо є активне вікно і воно не мінімізоване, додати тікет туди
        const activeWindow = getActiveWindow();
        if (activeWindow && !activeWindow.isMinimized) {
            addTicketToWindow(activeWindow.id, ticket);
            setActiveWindow(activeWindow.id);
            return activeWindow.id;
        }

        // Інакше створити нове вікно
        return openWindow(ticket);
    };

    /**
     * Оновити дані тікету у вікні
     * @param {String} windowId - ID вікна
     * @param {Number} tabIndex - Індекс табу
     * @param {Object} updatedTicket - Оновлені дані тікету
     */
    const updateTicketData = (windowId, tabIndex, updatedTicket) => {
        const window = storeS.ticketWindows.windows.find(w => w.id === windowId);
        if (window && window.tickets[tabIndex]) {
            // Оновлюємо дані тікету, зберігаючи реактивність
            Object.assign(window.tickets[tabIndex], updatedTicket);
        }
    };

    return {
        // Стан
        windows: computed(() => storeS.ticketWindows.windows),
        minimizedWindows: computed(() => storeS.ticketWindows.minimizedWindows),
        activeWindowId: computed(() => storeS.ticketWindows.activeWindowId),

        // Методи
        openWindow,
        closeWindow,
        minimizeWindow,
        restoreWindow,
        setActiveWindow,
        addTicketToWindow,
        closeTicketTab,
        setActiveTab,
        getActiveWindow,
        findOpenedTicket,
        openTicket,
        updateTicketData
    };
}
